
/**
 * A component that renders an IMO number as a clickable link.
 * When clicked, it selects the corresponding vessel for output.
 * 
 * @component
 * @example
 * ```tsx
 * <ImoRenderer imoNumber={9876543} />
 * <ImoRenderer imoNumber={9876543} displayValue="VESSEL NAME" className="custom-class" />
 * ```
 * 
 * @param {object} props - Component props
 * @param {number} props.imoNumber - The IMO number of the vessel
 * @param {string} [props.displayValue] - Optional text to display instead of the IMO number
 * @param {string} [props.className] - Optional CSS class name for custom styling
 * 
 * @returns {React.ReactElement|null} A clickable link displaying the IMO number or custom text,
 * or null if no IMO number is provided
 */
import React from 'react';
import { Link } from '@heroui/react';
import { useAppDispatch, useAppSelector } from '../../../core/rootHooks';
import { selectSubscribedMsiVesselByImoNumber } from '../../sharedSelectors';
import { AppDispatch, RootState } from '../../../core/store';
import { EOutputType, ESelectionType } from '../../sharedEnums';
import { ISelectedVesselsFormData, IVesselFormData } from '../../sharedTypes';
import { setSelectedVesselsForOutputRequest } from '../../../features/search/searchSlice';
import { showWarningAlert } from '../../../services/globalAlertService';
import { setTab } from '../../../routing/pageContextSlice';

interface ImoRendererProps {
	imoNumber: number;
	displayValue?: string; // Optional, in case you want to use the displayValue prop in order to show a different text
	className?: string; // Optional, to allow custom styling
	fromComponent?: string; // Optional, to indicate the source of the IMO number
}

export const ImoRenderer: React.FC<ImoRendererProps> = ({ imoNumber, displayValue, className, fromComponent }) => {
	if (!imoNumber) {
		return null;
	}

	const dispatch = useAppDispatch<AppDispatch>();

	const vesselData = useAppSelector((state: RootState) => selectSubscribedMsiVesselByImoNumber(state, imoNumber));

	const onPress = () => {
		if (vesselData) {
			const outputSelection: ISelectedVesselsFormData = {
				selectedVesselsList: [
					{
						name: vesselData.vesselName,
						imoNumber: vesselData.imoNumber,
						sectorName: vesselData.sectorName,
					},
				] as IVesselFormData[],
				outputType: EOutputType.Vessel,
				selectionType: ESelectionType.Vessel,
				selectionName: `${vesselData.imoNumber} - ${vesselData.vesselName}`,
				selectionTypeObject: {
					name: vesselData.vesselName,
					imoNumber: vesselData.imoNumber,
					sectorName: vesselData.sectorName,
				} as IVesselFormData,
			};

			if (fromComponent==='recentSales') {
				dispatch(setTab('overview'));
			}

			dispatch(setSelectedVesselsForOutputRequest({ outputSelection }));
		} else {
			showWarningAlert('Vessel not found');
		}
	};

	return (
		<Link
			onPress={onPress}
			underline="always"
			className={`text-msi-darkgrey-color-600 ${className}`}
			size="sm"
			title="Click to select vessel as output"
		>
			<span className="cursor-pointer">{displayValue || imoNumber}</span>
		</Link>
	);
};

export default ImoRenderer;
